import { jwtDecode } from "jwt-decode";

import { store } from "./store.js";

const API_BASE = process.env.VUE_APP_API_BASE || "https://admin.trueeye.co/api/v1";
const CORE_BASE = process.env.VUE_APP_CORE_BASE || "https://core.trueeye.co";

// ── Shared API helper ──────────────────────────────────────────────────
export const authedFetch = async (url, options = {}) => {
  const accessToken = sessionStorage.getItem("token");
  if (!accessToken) {
    throw new Error("No access token found in session.");
  }
  const headers = new Headers(options.headers || {});
  headers.set("Authorization", `Bearer ${accessToken}`);

  // Impersonation support
  const impersonateUserId = sessionStorage.getItem("impersonating_user_id");
  if (impersonateUserId) {
    headers.set("X-Impersonate-User", impersonateUserId);
  }

  if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      redirect: "follow",
    });

    // Auto-redirect to login if token is expired or unauthorized (TC30)
    if (response.status === 401) {
      sessionStorage.clear();
      window.location.href = "/login";
      return response;
    }

    if (!response.ok) {
      const errorText = await response.text();
      store.errorStatus = response.status;

      let parsedMessage = errorText;
      try {
        const json = JSON.parse(errorText);
        parsedMessage =
          json.errorMessage || json.error || json.message || errorText;
      } catch (e) {
        // Fallback to raw text if it's not JSON
      }

      store.errorMessage = parsedMessage || response.statusText;
      throw new Error(
        parsedMessage || `${response.status} ${response.statusText}`,
      );
    }
    return response;
  } catch (error) {
    if (!store.errorMessage) {
      store.errorMessage = error.message;
    }
    throw error;
  }
};

// ── Shared constants & role helper ─────────────────────────────────────
export const DEFAULT_ROLES = [
  "offline_access",
  "uma_authorization",
  "default-roles-master",
  "create-realm",
];

export const fetchUserRolesFiltered = async (userId) => {
  try {
    const response = await authedFetch(
      `${API_BASE}/admin/users/${userId}/role-mappings/realm`,
    );

    const roles = await response.json();
    const custom = roles
      .map((r) => r.name)
      .filter((name) => !DEFAULT_ROLES.includes(name));
    return custom.length > 0 ? custom.join(", ") : "User";
  } catch {
    return "User";
  }
};

export const isAdminRole = (roles) => {
  if (!roles) return false;
  let userRoles = [];
  if (Array.isArray(roles)) {
    userRoles = roles;
  } else if (typeof roles === "string") {
    userRoles = roles.split(",").map((r) => r.trim());
  } else {
    userRoles = [roles];
  }

  return userRoles.some((role) => {
    if (typeof role !== "string") return false;
    const r = role.toLowerCase();
    return (
      r === "admin" ||
      r === "superadmin" ||
      r === "super_admin" ||
      r === "scientist" ||
      r === "data_scientist" ||
      r.endsWith("_admin") ||
      r.endsWith("_staff")
    );
  });
};

export const isSuperAdminRole = (roles) => {
  if (!roles) return false;
  const values = Array.isArray(roles)
    ? roles
    : typeof roles === "string"
      ? roles.split(",").map((role) => role.trim())
      : [roles];
  return values.some((role) =>
    typeof role === "string" &&
    ["admin", "superadmin", "super_admin", "platform_admin", "trueeye_admin"].includes(role.toLowerCase()),
  );
};

export const getRoleCategory = (roles) => {
  const values = Array.isArray(roles) ? roles : typeof roles === "string" ? roles.split(",") : roles ? [roles] : [];
  const normalized = values.map((role) => String(role).trim().toLowerCase());
  if (normalized.some((role) => role === "admin" || role === "superadmin" || role.endsWith("_admin"))) return "Admin";
  if (normalized.some((role) => role.endsWith("_staff") || role === "scientist" || role === "data_scientist")) return "Staff";
  return "User";
};

export const loginUser = async (username, password) => {
  const loginHeader = new Headers();
  loginHeader.append("Content-Type", "application/json");

  const requestOptions = {
    method: "POST",
    headers: loginHeader,
    body: JSON.stringify({ username, password }),
    redirect: "follow",
  };

  try {
    const response = await fetch(`${API_BASE}/auth/login`, requestOptions);

    const result = await response.json();

    if (result.access_token) {
      const decoded = jwtDecode(result.access_token);
      const userRoles = decoded.realm_access?.roles || [];
      const isAdmin = isAdminRole(userRoles);

      if (!isAdmin) {
        return {
          success: false,
          error: "Access denied: Unauthorized role",
        };
      }

      // Store user data in session storage only for authorized roles
      sessionStorage.setItem("token", result.access_token);
      sessionStorage.setItem("name", decoded.preferred_username);
      sessionStorage.setItem(
        "fullname",
        decoded.name || decoded.preferred_username,
      );

      // Log the login action
      try {
        await saveAction({
          action: "Login",
          data1: "",
          data2: "",
        });
      } catch (logErr) {
        console.warn("Could not log action:", logErr);
      }

      return {
        success: true,
        user: decoded,
        token: result.access_token,
        isAdmin: true,
      };
    } else {
      return {
        success: false,
        error: result.error || "Login failed",
      };
    }
  } catch (error) {
    console.error("Login error:", error);
    return {
      success: false,
      error: "Network error occurred",
    };
  }
};

export async function getLogData() {
  try {
    const response = await authedFetch(`${API_BASE}/audit-logs?limit=500`);
    if (!response.ok) return [];

    const logs = await response.json();
    if (!Array.isArray(logs)) return [];

    const formattedLogs = logs.map((log) => {
      let data1 = "";
      let data2 = "";
      let rawChanges = {};
      try {
        if (log.changes) {
          const parsed = JSON.parse(log.changes);
          rawChanges = parsed;
          data1 = parsed.data1 || parsed.name || parsed.username || "";
          data2 = parsed.data2 || parsed.email || parsed.role || "";
        }
      } catch (e) {
        console.warn("Failed to parse log changes:", e);
      }

      return {
        timestamp: log.createdAt,
        action: log.action,
        data1: data1,
        data2: data2,
        rawChanges: rawChanges,
        username: log.actorId, // Since we don't have username from DB, show ID
        name: log.targetType,
      };
    });

    return { data: formattedLogs };
  } catch (err) {
    console.error(err);
    return { data: [] };
  }
}

export const fetchUserSiteFiltered = async (userId) => {
  try {
    const response = await authedFetch(
      `${API_BASE}/admin/users/${userId}/groups`,
    );

    const groups = await response.json();
    return groups.map((g) => g.name).join(", ");
  } catch {
    return "";
  }
};

// Fetch all users from the system (Supports pagination via BFF)
export const fetchAllUsers = async (
  first = 0,
  max = 100,
  search = "",
  fetchDetails = true,
) => {
  try {
    const params = new URLSearchParams();
    if (first !== null) params.append("first", first);
    if (max !== null) params.append("max", max);
    if (search) params.append("search", search);

    const queryString = params.toString() ? `?${params.toString()}` : "";

    const response = await authedFetch(`${API_BASE}/admin/users${queryString}`);

    const result = await response.json();

    if (Array.isArray(result)) {
      if (!fetchDetails) {
        return result.map((user) => ({
          id: user.id,
          name: user.username,
          email: user.email || "",
          role: "User",
          site: "",
          enabled: user.enabled !== false,
        }));
      }

      return result.map((user) => ({
        id: user.id,
        name: user.username,
        email: user.email || "",
        // Use enriched data from backend if available, fallback to individual calls if needed
        role: user.roles || [],
        site: (user.groups || []).join(", "),
        enabled: user.enabled !== false,
        // Kept flexible for deployments that enrich users with dealer scope.
        dealer: user.dealerName || user.dealer || "",
      }));
    }

    return [];
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

export const fetchUsersCount = async (search = "") => {
  try {
    const params = new URLSearchParams();
    if (search) params.append("search", search);

    const queryString = params.toString() ? `?${params.toString()}` : "";
    const response = await authedFetch(
      `${API_BASE}/admin/users/count${queryString}`,
    );

    const count = await response.text();
    return parseInt(count, 10) || 0;
  } catch (error) {
    console.error("Error fetching users count:", error);
    return 0;
  }
};

// Fetch all sites/groups from the system (Supports pagination via BFF)
export const fetchAllSites = async (first = 0, max = 100) => {
  try {
    const params = new URLSearchParams();
    if (first !== null) params.append("first", first);
    if (max !== null) params.append("max", max);

    const queryString = params.toString() ? `?${params.toString()}` : "";

    const response = await authedFetch(
      `${API_BASE}/admin/groups${queryString}`,
    );

    const result = await response.json();

    if (Array.isArray(result)) {
      return result.map((site) => ({ id: site.id, name: site.name }));
    }

    return [];
  } catch (error) {
    console.error("Error fetching sites:", error);
    throw error;
  }
};

// Fetch all members of a specific site (group) (Supports pagination)
export const fetchSiteMembers = async (
  siteId,
  first = 0,
  max = 100,
  fetchDetails = true,
) => {
  try {
    const params = new URLSearchParams();
    if (first !== null) params.append("first", first);
    if (max !== null) params.append("max", max);

    const queryString = params.toString() ? `?${params.toString()}` : "";

    const response = await authedFetch(
      `${API_BASE}/admin/groups/${siteId}/members${queryString}`,
    );

    const result = await response.json();

    if (Array.isArray(result)) {
      if (!fetchDetails) {
        return result.map((user) => ({
          id: user.id,
          name: user.username,
          email: user.email || "",
          role: "User",
        }));
      }

      return await Promise.all(
        result.map(async (user) => ({
          id: user.id,
          name: user.username,
          email: user.email || "",
          role: await fetchUserRolesFiltered(user.id),
        })),
      );
    }

    return [];
  } catch (error) {
    console.error(`Error fetching members for site ${siteId}:`, error);
    throw error;
  }
};

// Add users to a site/group
export const addUsersToSite = async (siteId, selectedUsers) => {
  try {
    await Promise.all(
      selectedUsers.map((user) =>
        authedFetch(`${API_BASE}/admin/users/${user.id}/groups/${siteId}`, {
          method: "PUT",
        }),
      ),
    );

    await saveAction({
      action: "Join Site",
      data1: sessionStorage.getItem("siteSelected") || siteId,
      data2: selectedUsers.map((u) => u.name).join(", "),
    });

    return { success: true };
  } catch (error) {
    console.error("Error adding users to site:", error);
    return { success: false, error: error.message };
  }
};

// Remove users from a site/group
export const removeMembersFromSite = async (
  siteId,
  selectedUsers,
  siteName = null,
) => {
  try {
    await Promise.all(
      selectedUsers.map((user) =>
        authedFetch(`${API_BASE}/admin/users/${user.id}/groups/${siteId}`, {
          method: "DELETE",
        }),
      ),
    );

    await saveAction({
      action: "Leave Site",
      data1: siteName || sessionStorage.getItem("siteSelected") || siteId,
      data2: selectedUsers.map((u) => u.name).join(", "),
    });

    return { success: true };
  } catch (error) {
    console.error("Error removing users from site:", error);
    return { success: false, error: error.message };
  }
};

// Fetch site/group details including attributes
export const fetchSiteDetails = async (siteId) => {
  try {
    const response = await authedFetch(`${API_BASE}/admin/groups/${siteId}`);

    const result = await response.json();

    if (result.attributes) {
      const attributesArray = Object.entries(result.attributes)
        .map(([key, value]) => ({ key, value: value[0] || "" }))
        .sort((a, b) => a.key.localeCompare(b.key));

      return {
        success: true,
        data: {
          attributes: attributesArray,
          name: result.name || "",
          path: result.path || "",
          otpEnabled: result.attributes["CFG_OTP"]?.[0] === "True",
        },
      };
    }

    return {
      success: true,
      data: {
        attributes: [],
        name: result.name || "",
        path: result.path || "",
        otpEnabled: false,
      },
    };
  } catch (error) {
    console.error("Error fetching site details:", error);
    return { success: false, error: error.message };
  }
};

export const fetchFilenamePresets = async () => {
  const response = await authedFetch(`${API_BASE}/admin/filename-presets`);
  return response.json();
};

export const createFilenamePreset = async (preset) => {
  const response = await authedFetch(`${API_BASE}/admin/filename-presets`, {
    method: "POST",
    body: JSON.stringify(preset),
  });
  return response.json();
};

export const updateFilenamePreset = async (id, preset) => {
  const response = await authedFetch(`${API_BASE}/admin/filename-presets/${id}`, {
    method: "PUT",
    body: JSON.stringify(preset),
  });
  return response.json();
};

export const deleteFilenamePreset = async (id) => {
  const response = await authedFetch(`${API_BASE}/admin/filename-presets/${id}`, {
    method: "DELETE",
  });
  return response.json();
};

// Update site attributes
export const updateSiteAttributes = async (
  siteId,
  attributes,
  siteName,
  sitePath,
) => {
  try {
    // Get current group data to compare changes
    const groupRes = await authedFetch(`${API_BASE}/admin/groups/${siteId}`);

    const currentGroup = await groupRes.json();

    const newAttributes = Object.fromEntries(
      attributes.map(({ key, value }) => [key, [value]]),
    );

    // Track what changed for logging
    const changed = Object.entries(newAttributes)
      .filter(
        ([key, val]) => (currentGroup.attributes?.[key]?.[0] || "") !== val[0],
      )
      .map(
        ([key, val]) =>
          `${key}: ${currentGroup.attributes?.[key]?.[0] || ""} → ${val[0]}`,
      );

    if (!changed.length) {
      return { success: true, message: "No changes detected" };
    }

    await authedFetch(
      `${API_BASE}/admin/groups/${siteId}`,

      {
        method: "PUT",
        body: JSON.stringify({
          name: siteName,
          path: sitePath,
          subGroupCount: 0,
          subGroups: [],
          attributes: newAttributes,
          realmRoles: ["trueeye_customer"],
        }),
      },
    );

    await saveAction({
      action: "Update Site Attributes",
      data1: siteName,
      data2: changed.join(" | "),
    });

    return { success: true, changes: changed };
  } catch (error) {
    console.error("Error updating site attributes:", error);
    return { success: false, error: error.message };
  }
};

// Create new site attribute
export const createSiteAttribute = async (
  siteId,
  attributes,
  siteName,
  sitePath,
  newAttributeKey,
  newAttributeValue,
) => {
  try {
    const attributesObject = attributes.reduce((acc, { key, value }) => {
      acc[key] = [value];
      return acc;
    }, {});
    attributesObject[newAttributeKey] = [newAttributeValue];

    await authedFetch(
      `${API_BASE}/admin/groups/${siteId}`,

      {
        method: "PUT",
        body: JSON.stringify({
          name: siteName,
          path: sitePath,
          subGroupCount: 0,
          subGroups: [],
          attributes: attributesObject,
          realmRoles: ["trueeye_customer"],
        }),
      },
    );

    await saveAction({
      action: "Create Site Attribute",
      data1: siteName,
      data2: `${newAttributeKey}: ${newAttributeValue}`,
    });

    return { success: true };
  } catch (error) {
    console.error("Error creating site attribute:", error);
    return { success: false, error: error.message };
  }
};

// Create new site/group
export const createSite = async (siteName) => {
  try {
    const accessToken = sessionStorage.getItem("token");
    const dbName = "trueeye_" + siteName.toLowerCase();
    const siteNameLower = siteName.toLowerCase();

    // 1. Call core service to create the database
    const coreResponse = await fetch(`${CORE_BASE}/admin/createNewSite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        site_name: siteNameLower,
      }),
      redirect: "follow",
    });

    if (!coreResponse.ok) {
      const errText = await coreResponse.text();
      throw new Error(
        `Failed to create database via Core Service: ${
          errText || coreResponse.statusText
        }`,
      );
    }

    // 2. Call Keycloak API to create the group/site
    const response = await authedFetch(
      `${API_BASE}/admin/groups`,

      {
        method: "POST",
        body: JSON.stringify({
          name: siteName,
          path: "/" + siteName,
          subGroupCount: 0,
          subGroups: [],
          attributes: {
            CFG_ENCODING: ["UTF-8"],
            CFG_IMAGE_FILENAME_FORMAT_SPLIT: ["_"],
            CFG_AUTO_FIELD: ["False"],
            CFG_CUSTOMER_NAME: [siteName + " Hospital"],
            CFG_MODEL_DR5CLS_P34_THRESHOLD: ["0.4"],
            CFG_MODEL_CKD_THRESHOLD: ["0.7"],
            CFG_OTP_DIGITS: ["6"],
            CFG_CUSTOMER_DB: [dbName],
            CFG_MODEL_UNGRADE_THRESHOLD: ["0.65"],
            CFG_MODEL_DR5CLS_3PLUS4_THRESHOLD: ["0.6"],
            CFG_IMAGE_FILENAME_FORMAT: [
              "[HN]_[NAME]_Retina_[ODOS]_[IMAGE_DATE]_[RUNNING].[FILE_TYPE]",
            ],
            CFG_MODEL_DR5CLS_P2_THRESHOLD: ["0.85"],
            CFG_ENABLE_MENU_SINGLE_UPLOAD: ["True"],
            CFG_ENABLE_NID: ["False"],
            CFG_MODEL_DR_WEIGHT: ["20"],
            CFG_MODEL_CKD5Y_THRESHOLD: ["0.6"],
            CFG_MODEL_DR2_THRESHOLD: ["0.725"],
            CFG_MODEL_DR_VERSION: ["production"],
            CFG_OTP: ["True"],
            CFG_API_KEY: ["jkotmeVWw626e_h9y1es7S8qL3yGho"],
            CFG_API_SECRET: ["mT7Uay945Of9UPWS1aXefh7-xpPHbH"],
            CFG_CUSTOMER_ID: [""],
            CFG_ENABLE_MODEL_CKD: ["True"],
            CFG_ENABLE_MODEL_CKD5Y: ["True"],
            CFG_ENABLE_MODEL_DM: ["False"],
            CFG_ENABLE_MODEL_DR: ["True"],
            CFG_ENABLE_MODEL_GLAUCOMA: ["True"],
            CFG_ENABLE_PACS: ["False"],
            CFG_PACS_USERNAME: [""],
            CFG_PACS_PASSWORD: [""],
            CFG_PACS_URL: [""],
            CFG_MASKING: ["False"],
            CFG_OTP_CHANNEL: ["email"],
            CFG_LINE_CHANNEL_ID: [""],
            CFG_LINE_CHANNEL_SECRET: [""],
            CFG_LINE_LIFF_URL: [""],
          },
          realmRoles: ["trueeye_customer"],
        }),
      },
    );

    await saveAction({
      action: "Create Site",
      data1: siteName,
      data2: "",
    });

    return { success: true, result: await response.text() };
  } catch (error) {
    console.error("Error in createSite function:", error);
    return { success: false, error: error.message };
  }
};

// Delete site/group
export const deleteSite = async (siteId, siteName) => {
  try {
    const response = await authedFetch(`${API_BASE}/admin/groups/${siteId}`, {
      method: "DELETE",
    });

    // Log the action
    await saveAction({
      action: "Delete Site",
      data1: siteName,
      data2: `Site ID: ${siteId}`,
    });

    return { success: true, result: await response.text() };
  } catch (error) {
    console.error("Error in deleteSite function:", error);
    return { success: false, error: error.message };
  }
};

// Check user authentication
export const checkUserAuthentication = () => {
  const token = sessionStorage.getItem("token");
  return !!token;
};

export async function saveAction({ action, data1 = "", data2 = "" }) {
  try {
    await authedFetch(`${API_BASE}/audit-logs`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ action, data1, data2 }),
    });
  } catch (error) {
    console.error("Failed to save action log:", error);
  }
}
// ── Impersonation Helpers ──────────────────────────────────────────────
export const startImpersonation = (user) => {
  sessionStorage.setItem("impersonating_user_id", user.id);
  sessionStorage.setItem("impersonating_user_name", user.name);
  window.location.reload();
};

export const stopImpersonation = () => {
  sessionStorage.removeItem("impersonating_user_id");
  sessionStorage.removeItem("impersonating_user_name");
  window.location.reload();
};

export const getImpersonatingUser = () => {
  const id = sessionStorage.getItem("impersonating_user_id");
  const name = sessionStorage.getItem("impersonating_user_name");
  return id ? { id, name } : null;
};
